<template>
  <Stack style="min-width: 600px;">
    <div class="tw-flex tw-justify-between tw-items-center">
      <Title>
        <span v-if="entityName">{{ entityName }}'s Meetings</span>
        <span v-else>Meetings Linked to This Person</span>
      </Title>
      <router-link
        to="/advisor/meetings"
        class="stk-button is-primary"
        @click.native="$emit('close')"
      >
        View All Meetings
      </router-link>
    </div>

    <template v-if="hasFetchedMeetings">
      <Callout v-if="meetings.length === 0" status="info" iconBefore="fas fa-calendar">
        No meetings found
      </Callout>

      <div
        v-else
        class="meetings-list"
      >
        <div
          v-for="meeting in meetings"
          :key="meeting.id"
          class="box meeting-card"
        >
          <Level>
            <LevelLeft>
              <router-link
                :to="{
                  name: 'entity-note-taking',
                  params: { entityExternalId },
                  query: { meetingId: meeting.id }
                }"
                class="meeting-link"
              >
                <div class="meeting-title">
                  <span class="icon-text">
                    <span class="icon">
                      <i class="fas fa-comments" />
                    </span>
                    <span :class="['is-size-5', meeting.summary ? 'has-text-grey' : 'has-text-weight-medium']">
                      {{ meeting.title || 'Untitled Meeting' }}
                    </span>
                  </span>
                </div>
              </router-link>
            </LevelLeft>
            <LevelRight>
              <MeetingStatus
                :status="meeting.status"
                :failure-reason="meeting.failure_reason || 'We were unable to process this meeting'"
              />
            </LevelRight>
          </Level>

          <Level>
            <LevelLeft>
              {{ formatDate(meeting.meeting_date) }}
            </LevelLeft>
            <LevelRight>
              <DeleteMeeting
                :meeting="meeting"
                @deleted="handleDelete"
                @click.stop
              >
                <i
                  class="fa fa-trash tw-text-rose-600 hover:tw-text-rose-700 tw-cursor-pointer"
                  title="Delete meeting"
                />
              </DeleteMeeting>
            </LevelRight>
          </Level>
        </div>
      </div>
    </template>

    <LoaderWithText v-else>
      Fetching meetings…
    </LoaderWithText>
  </Stack>
</template>

<script>
import MeetingStatus from '@features/meetings/components/MeetingStatus.vue';
import DeleteMeeting from '@frontend/ui/features/meetings/components/DeleteMeeting.vue';
import {
  Callout,
  Level,
  LevelLeft,
  LevelRight,
  LoaderWithText,
  Stack,
  Title,
} from '@stakeui';

export default {
  name: 'MeetingsListModal',
  components: {
    MeetingStatus,
    DeleteMeeting,
    Callout,
    Level,
    LevelLeft,
    LevelRight,
    LoaderWithText,
    Stack,
    Title,
  },
  props: {
    entityExternalId: {
      type: String,
      required: true,
    },
    entityName: {
      type: String,
      default: '',
    },
    hasFetchedMeetings: {
      type: Boolean,
      default: false,
    },
    meetings: {
      type: Array,
    },
  },
  emits: ['close'],
  methods: {
    formatDate(date) {
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }).format(new Date(date));
    },
    handleDelete() {
      // Redirect to the note-taking tab
      const url = this.$router.resolve({
        name: 'entity-note-taking',
        params: { entityExternalId: this.entityExternalId },
      }).href;

      window.location.href = url;
    },
  },
};
</script>

<style scoped>
.meetings-list {
  max-height: 70vh;
  overflow-y: auto;
  margin: -0.75rem;
  padding: 0.75rem;
}

.meeting-link {
  display: block;
  text-decoration: none !important;
  color: inherit;
}

.meeting-card {
  transition: all 0.2s ease;
  border: 1px solid transparent;
  margin-bottom: 0.75rem;
}

.meeting-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5em 1em -0.125em rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.02);
  border-color: #dbdbdb;
}

.meeting-title {
  display: flex;
  align-items: center;
}

.icon-text {
  align-items: center;
}

.icon-text .icon {
  margin-right: 0.5rem;
  color: #7a7a7a;
}

/* Custom scrollbar for webkit browsers */
.meetings-list::-webkit-scrollbar {
  width: 8px;
}

.meetings-list::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 4px;
}

.meetings-list::-webkit-scrollbar-thumb {
  background: #dbdbdb;
  border-radius: 4px;
}

.meetings-list::-webkit-scrollbar-thumb:hover {
  background: #b5b5b5;
}

.box {
  min-width: 600px;
}
</style>
